/**
 * Client-side photo preparation (spec section 8): apply EXIF orientation,
 * scale to 2048 px (JPEG 0.82) plus a 400 px thumbnail, and re-encode through
 * a canvas, which drops every metadata segment including GPS. Browser-only;
 * the size math is in imageMath.ts and tested there.
 */
import { THUMBNAIL_EDGE_PX, UPLOAD_MAX_EDGE_PX } from '@fsv/core';
import { PHOTO_JPEG_QUALITY, photoSizes, THUMB_JPEG_QUALITY, type NewImage } from '@fsv/data';

export const JPEG_QUALITY = PHOTO_JPEG_QUALITY;

async function decode(file: Blob): Promise<ImageBitmap | HTMLImageElement> {
  if ('createImageBitmap' in window) {
    try {
      return await createImageBitmap(file, { imageOrientation: 'from-image' });
    } catch {
      /* fall through: older Safari, or a format only <img> can decode */
    }
  }
  const url = URL.createObjectURL(file);
  try {
    const img = new Image();
    img.decoding = 'async';
    img.src = url;
    await img.decode();
    return img;
  } finally {
    URL.revokeObjectURL(url);
  }
}

function encode(source: CanvasImageSource, width: number, height: number, quality: number): string {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('canvas unavailable');
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(source, 0, 0, width, height);
  return canvas.toDataURL('image/jpeg', quality);
}

export async function preparePhoto(file: Blob, isOverview = false): Promise<NewImage> {
  const image = await decode(file);
  const w = 'naturalWidth' in image ? image.naturalWidth : image.width;
  const h = 'naturalHeight' in image ? image.naturalHeight : image.height;
  const { main, thumb } = photoSizes(w, h, { main: UPLOAD_MAX_EDGE_PX, thumb: THUMBNAIL_EDGE_PX });
  const result: NewImage = {
    dataUrl: encode(image, main.width, main.height, JPEG_QUALITY),
    thumbDataUrl: encode(image, thumb.width, thumb.height, THUMB_JPEG_QUALITY),
    width: main.width,
    height: main.height,
    isOverview,
  };
  if ('close' in image) image.close();
  return result;
}
